import React, { useMemo } from 'react';
import Tippy from '@tippyjs/react';
import { CustomerSentimentTimelinePoint } from '../shared/types';
import {
  getMessageSentimentColor,
  getMessageSentimentDisplayLabel,
  getMessageSentimentTooltip,
} from '../shared/messageSentiment';
import './CustomerSentimentTimeline.scss';

interface CustomerSentimentTimelineProps {
  points: CustomerSentimentTimelinePoint[];
}

interface TimelinePosition {
  point: CustomerSentimentTimelinePoint;
  x: number;
  y: number;
}

const SVG_WIDTH = 320;
const SVG_HEIGHT = 112;
const HORIZONTAL_PADDING = 12;
const VERTICAL_PADDING = 16;
const PLOT_WIDTH = SVG_WIDTH - HORIZONTAL_PADDING * 2;
const PLOT_HEIGHT = SVG_HEIGHT - VERTICAL_PADDING * 2;

function formatTimelineTime(timestamp: string): string {
  return new Date(timestamp).toISOString().slice(11, 16);
}

function hasValidTimelineData(points: CustomerSentimentTimelinePoint[]): boolean {
  let previousTimestamp = Number.NEGATIVE_INFINITY;
  return points.every(({ score, timestamp }) => {
    const parsedTimestamp = Date.parse(timestamp);
    const isValid =
      Number.isFinite(score) &&
      score >= -1 &&
      score <= 1 &&
      Number.isFinite(parsedTimestamp) &&
      parsedTimestamp >= previousTimestamp;
    previousTimestamp = parsedTimestamp;
    return isValid;
  });
}

function getTimelinePositions(points: CustomerSentimentTimelinePoint[]): TimelinePosition[] {
  const timestamps = points.map(({ timestamp }) => Date.parse(timestamp));
  const firstTimestamp = timestamps[0];
  const lastTimestamp = timestamps[timestamps.length - 1];
  const timeRange = lastTimestamp - firstTimestamp;

  return points.map((point, index) => {
    const x =
      points.length === 1
        ? SVG_WIDTH / 2
        : timeRange === 0
        ? HORIZONTAL_PADDING + (PLOT_WIDTH * index) / (points.length - 1)
        : HORIZONTAL_PADDING + (PLOT_WIDTH * (timestamps[index] - firstTimestamp)) / timeRange;
    const y = VERTICAL_PADDING + ((1 - point.score) / 2) * PLOT_HEIGHT;
    return { point, x, y };
  });
}

const CustomerSentimentTimeline: React.FC<CustomerSentimentTimelineProps> = ({ points }) => {
  const isValid = hasValidTimelineData(points);
  const positions = useMemo(() => (isValid ? getTimelinePositions(points) : []), [isValid, points]);
  const linePath = positions
    .map(({ x, y }, index) => `${index === 0 ? 'M' : 'L'} ${x} ${y}`)
    .join(' ');

  return (
    <section className="ca-sentiment-timeline" aria-labelledby="ca-sentiment-timeline-title">
      <div className="ca-sentiment-timeline__header">
        <h2 className="ca-sentiment-timeline__title" id="ca-sentiment-timeline-title">
          CUSTOMER SENTIMENT OVER TIME
        </h2>
        <div className="ca-sentiment-timeline__legend" aria-label="Sentiment legend">
          {['Positive', 'Neutral', 'Negative'].map((sentiment) => (
            <span key={sentiment} className="ca-sentiment-timeline__legend-item">
              <span
                className="ca-sentiment-timeline__legend-dot"
                style={{ background: getMessageSentimentColor(sentiment) }}
                aria-hidden="true"
              />
              {sentiment.toLowerCase()}
            </span>
          ))}
        </div>
      </div>

      {points.length === 0 && (
        <div className="ca-sentiment-timeline__empty">No customer sentiment data available.</div>
      )}

      {points.length > 0 && !isValid && (
        <div className="ca-sentiment-timeline__error" role="alert">
          Customer sentiment timeline data is invalid.
        </div>
      )}

      {positions.length > 0 && (
        <div className="ca-sentiment-timeline__chart">
          <svg
            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
            role="group"
            aria-label={`Customer sentiment timeline with ${positions.length} points`}
          >
            <rect
              className="ca-sentiment-timeline__range"
              x={HORIZONTAL_PADDING}
              y={VERTICAL_PADDING}
              width={PLOT_WIDTH}
              height={PLOT_HEIGHT}
              rx="4"
            />
            <line
              className="ca-sentiment-timeline__boundary"
              x1={HORIZONTAL_PADDING}
              x2={SVG_WIDTH - HORIZONTAL_PADDING}
              y1={VERTICAL_PADDING}
              y2={VERTICAL_PADDING}
            />
            <line
              className="ca-sentiment-timeline__zero-line"
              x1={HORIZONTAL_PADDING}
              x2={SVG_WIDTH - HORIZONTAL_PADDING}
              y1={SVG_HEIGHT / 2}
              y2={SVG_HEIGHT / 2}
            />
            <line
              className="ca-sentiment-timeline__boundary"
              x1={HORIZONTAL_PADDING}
              x2={SVG_WIDTH - HORIZONTAL_PADDING}
              y1={SVG_HEIGHT - VERTICAL_PADDING}
              y2={SVG_HEIGHT - VERTICAL_PADDING}
            />
            {positions.length > 1 && <path className="ca-sentiment-timeline__line" d={linePath} />}
            {positions.map(({ point, x, y }) => {
              const label = getMessageSentimentDisplayLabel(point.sentiment);
              const time = formatTimelineTime(point.timestamp);
              const ariaLabel = `${getMessageSentimentTooltip(point.sentiment)} at ${time}`;

              return (
                <Tippy
                  key={point.message_id}
                  className="ca-sentiment-timeline-tooltip"
                  content={
                    <div className="ca-sentiment-timeline__tooltip">
                      <div className="ca-sentiment-timeline__tooltip-header">
                        <span
                          className="ca-sentiment-timeline__tooltip-dot"
                          style={{ background: getMessageSentimentColor(point.sentiment) }}
                          aria-hidden="true"
                        />
                        <strong>{label}</strong>
                        <span>{time}</span>
                      </div>
                      <div className="ca-sentiment-timeline__tooltip-excerpt">{point.excerpt}</div>
                    </div>
                  }
                  placement="top"
                  zIndex={100000}
                >
                  <circle
                    className="ca-sentiment-timeline__point"
                    cx={x}
                    cy={y}
                    r="4.5"
                    fill={getMessageSentimentColor(point.sentiment)}
                    stroke="#fff"
                    strokeWidth="1.5"
                    tabIndex={0}
                    role="img"
                    aria-label={ariaLabel}
                  />
                </Tippy>
              );
            })}
          </svg>
        </div>
      )}
    </section>
  );
};

export default CustomerSentimentTimeline;
